"use client";

import dynamic from "next/dynamic";
import { usePostHog } from "@posthog/next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RawData } from "react-spreadsheet-import/types/types";
import { THEME } from "@/enums/profile";
import { useOrg } from "@/hooks/useOrg";
import { useUserProfile } from "@/hooks/useUserProfile";
import { ContactUploadCompletePayload } from "@/types/contacts";
import { PUBLIC_ENV_VARS } from "@/utils/common/environment";
import { GetSystemTheme } from "@/utils/common/general";
import { NormalizePhoneNumber } from "@/utils/common/phone";
import {
  OptionalPhoneNumberRegex,
  emailRegex,
  nameLooseRegex,
  stateRegex,
  zipRegex,
} from "@/utils/common/regex";
import { buildPreUploadFailureRow } from "@/utils/contacts/uploadFailures";
import { GetVariablesAllowed, GroupPhoneFields, IsCustomVariableValid } from "@/utils/contacts";
import {
  buildFallbackPhoneMappings,
  buildNonPhoneFieldMappings,
  buildPhoneCustomFieldMappings,
  buildPhoneFieldMappingsForHeaderSelect,
  buildPhoneMappingsFromFields,
  getUnknownHeaders,
  mapValidRowsToContactUploads,
  type UploadContactFieldType,
  type UploadContactPhoneFieldMapping,
} from "@/utils/contacts/uploadContactMapping";

const ReactSpreadsheetImport = dynamic(
  () => import("react-spreadsheet-import").then((mod) => mod.ReactSpreadsheetImport),
  {
    ssr: false,
  }
);

type FieldType = UploadContactFieldType;
type PhoneFieldMapping = UploadContactPhoneFieldMapping;

type UploadContactsProps = {
  isUploaderOpen: boolean;
  setIsUploaderOpen: (open: boolean) => void;
  onUploadComplete: (payload: ContactUploadCompletePayload) => Promise<void>;
  maxRecordsAllowed: number;
};

export default function UploadContacts({
  isUploaderOpen,
  setIsUploaderOpen,
  onUploadComplete,
  maxRecordsAllowed,
}: Readonly<UploadContactsProps>) {
  const { ui } = useOrg();
  const { userProfile } = useUserProfile();

  const { t } = useTranslation("contacts");
  const { t: ct } = useTranslation("common");
  const posthog = usePostHog();

  const [theme, setTheme] = useState<"dark" | "light">("light");

  useEffect(() => {
    if (userProfile.theme !== THEME.SYSTEM) {
      setTheme(userProfile.theme);
      return;
    }

    setTheme(GetSystemTheme());
  }, []);

  const DEFAULT_FIELDS: FieldType[] = [
    {
      // Visible in table header and when matching columns.
      label: ct("firstname"),
      // This is the key used for this field when we call onSubmit.
      key: "first_name",
      // Allows for better automatic column matching. Optional.
      alternateMatches: ["first name", "first", "name", "nombre", "firstname"],
      // Used when editing and validating information.
      fieldType: {
        // There are 3 types - "input" / "checkbox" / "select".
        type: "input",
      },
      // Used in the first step to provide an example of what data is expected in this field. Optional.
      example: "John",
      // Can have multiple validations that are visible in Validation Step table.
      validations: [
        {
          rule: "regex",
          value: nameLooseRegex,
          errorMessage: ct("firstnameRequired"),
          level: "error",
        },
      ],
    },
    {
      label: ct("lastname"),
      key: "last_name",
      alternateMatches: ["last name", "last", "surname", "apellido", "lastname"],
      fieldType: {
        type: "input",
      },
      example: "Doe",
    },
    {
      label: `📞 ${ct("phone")}`,
      key: "phone",
      alternateMatches: [
        "phonenumber",
        "phone number",
        "tel",
        "telephone",
        "cell",
        "mobile",
        "phone 1",
        "phone_1",
        "mobile_1",
        "cell_1",
        t("phone"),
        t("mobile"),
        t("cell"),
      ],
      fieldType: {
        type: "input",
      },
      example: "+1 (408) 555-0123",
      validations: [
        {
          rule: "regex",
          value: OptionalPhoneNumberRegex,
          errorMessage: ct("phoneNotValid"),
          level: "error",
        },
      ],
    },
    {
      label: `🏠 ${t("home")}`,
      key: "home",
      alternateMatches: ["home", "phone 2", t("home")],
      fieldType: {
        type: "input",
      },
      example: "+1 (408) 555-0123",
      validations: [
        {
          rule: "regex",
          value: OptionalPhoneNumberRegex,
          errorMessage: ct("phoneNotValid"),
          level: "error",
        },
      ],
    },
    {
      label: `🏢 ${t("office")}`,
      key: "work",
      alternateMatches: ["work", "office", "fax", "phone 3", t("office"), t("work")],
      fieldType: {
        type: "input",
      },
      example: "+1 (408) 555-0123",
      validations: [
        {
          rule: "regex",
          value: OptionalPhoneNumberRegex,
          errorMessage: ct("phoneNotValid"),
          level: "error",
        },
      ],
    },
    {
      label: `📠 ${t("fax")}`,
      key: "fax",
      alternateMatches: ["phone 4", t("fax")],
      fieldType: {
        type: "input",
      },
      example: "+1 (408) 555-0123",
      validations: [
        {
          rule: "regex",
          value: OptionalPhoneNumberRegex,
          errorMessage: ct("phoneNotValid"),
          level: "error",
        },
      ],
    },
    {
      label: ct("email"),
      key: "email",
      alternateMatches: ["email address"],
      fieldType: {
        type: "input",
      },
      example: PUBLIC_ENV_VARS.SUPPORT_EMAIL,
      validations: [
        {
          rule: "regex",
          value: emailRegex,
          errorMessage: ct("emailNotValid"),
          level: "warning",
        },
      ],
    },
    {
      label: ct("addressLine1"),
      key: "address_line_1",
      alternateMatches: ["address line 1", "address", "street"],
      fieldType: {
        type: "input",
      },
      example: "400 Broad St",
    },
    {
      label: ct("addressLine2"),
      key: "address_line_2",
      alternateMatches: ["address line 2", "suite", "building"],
      fieldType: {
        type: "input",
      },
      example: "Suite 100",
    },
    {
      label: ct("city"),
      key: "city",
      alternateMatches: [],
      fieldType: {
        type: "input",
      },
      example: "Miami",
    },
    {
      label: ct("state"),
      key: "state",
      alternateMatches: ["ST"],
      fieldType: {
        type: "input",
      },
      example: "FL",
      validations: [
        {
          rule: "regex",
          value: stateRegex,
          errorMessage: ct("stateNotValid"),
          level: "error",
        },
      ],
    },
    {
      label: ct("zip"),
      key: "zip",
      alternateMatches: ["zipcode", "zip code", "postal", "postal code", "area code"],
      fieldType: {
        type: "input",
      },
      example: "98109",
      validations: [
        {
          rule: "regex",
          value: zipRegex,
          errorMessage: ct("zipNotValid"),
          level: "error",
        },
      ],
    },
    {
      label: t("leadSource"),
      key: "lead_source",
      alternateMatches: ["lead source", "source", "referral", "lead"],
      fieldType: {
        type: "input",
      },
      example: "Project X",
    },
    {
      label: ct("companyName"),
      key: "business_name",
      alternateMatches: ["Business Name", "Company"],
      fieldType: {
        type: "input",
      },
      example: "Google",
    },
  ];

  const [fields, setFields] = useState<FieldType[]>(DEFAULT_FIELDS);

  const handleUploaderSubmit = async (uploadData: any) => {
    try {
      let currentPhoneFieldMappings: PhoneFieldMapping[] = buildPhoneMappingsFromFields(fields);

      if (currentPhoneFieldMappings.length === 0) {
        currentPhoneFieldMappings = buildFallbackPhoneMappings(
          uploadData?.all,
          GroupPhoneFields,
          t
        );
      }

      const validRows: any[] = uploadData?.validData || [];
      const { leads, preUploadFailures } = mapValidRowsToContactUploads({
        validRows,
        fields,
        defaultFields: DEFAULT_FIELDS,
        phoneMappings: currentPhoneFieldMappings,
        country: ui.country,
        normalizePhoneNumber: NormalizePhoneNumber,
        buildPreUploadFailureRow,
        preUploadFailureReason: t("uploadFailureInvalidPhone"),
        isCustomVariableValid: IsCustomVariableValid,
      });

      if (leads.length !== uploadData.validData.length) {
        console.warn("Some leads were not uploaded due to missing or malformed phone numbers", {
          leadsLength: leads.length,
          uploadDataLength: uploadData.validData.length,
        });
      }

      posthog.capture("Contacts Uploaded", {
        contactsUploadedValid: uploadData.validData.length,
        contactsUploadedInvalid: uploadData.invalidData.length,
        contactsUploadedAll: uploadData.all.length,
      });

      await onUploadComplete({
        leads,
        preUploadFailures,
      });
    } catch (ex) {
      console.error("Processing Contacts for upload::", ex);
      return;
    }
  };

  const handleHeaderSelect = async (headerValues: RawData, data: RawData[]) => {
    const unknownHeaders = getUnknownHeaders(headerValues, DEFAULT_FIELDS);

    // Group phone fields and non-phone fields
    const { phoneFields, nonPhoneFields } = GroupPhoneFields(
      unknownHeaders.map((h) => h.label),
      t
    );

    const phoneFieldMappings = buildPhoneFieldMappingsForHeaderSelect({
      phoneFields,
      unknownHeaders,
      data,
      t,
      phoneValidationRegex: OptionalPhoneNumberRegex,
      phoneNotValidMessage: ct("phoneNotValid"),
    });

    const phoneCustomFieldMappings = buildPhoneCustomFieldMappings({
      phoneFields,
      unknownHeaders,
      data,
    });

    const nonPhoneFieldMappings = buildNonPhoneFieldMappings({
      nonPhoneFields,
      unknownHeaders,
      data,
      getVariablesAllowed: GetVariablesAllowed,
    });

    // Combine all fields: default fields, phone fields, phone custom fields, and non-phone fields
    const allFields = [
      ...DEFAULT_FIELDS,
      ...phoneFieldMappings,
      ...phoneCustomFieldMappings,
      ...nonPhoneFieldMappings,
    ];

    setFields(allFields);

    return { headerValues, data };
  };

  if (!isUploaderOpen) {
    return null;
  }

  return (
    <ReactSpreadsheetImport
      isOpen={true}
      onClose={() => setIsUploaderOpen(false)}
      onSubmit={handleUploaderSubmit}
      fields={fields}
      maxRecords={maxRecordsAllowed >= 0 ? maxRecordsAllowed : undefined}
      isNavigationEnabled={true}
      selectHeaderStepHook={handleHeaderSelect}
      translations={{
        uploadStep: {
          title: t("uploadContacts"),
          manifestTitle: t("dataLookingFor"),
          manifestDescription: t("uploadManifestDescription"),
          maxRecordsExceeded: (maxRecords: string) => t("uploadManifestMax", { maxRecords }),
          dropzone: {
            title: t("fileTypes"),
            errorToastDescription: t("uploadRejected"),
            activeDropzoneTitle: t("dropTitle"),
            buttonTitle: t("selectFile"),
            loadingTitle: t("processing"),
          },
          selectSheet: {
            title: t("selectSheet"),
            nextButtonTitle: ct("next"),
            backButtonTitle: ct("back"),
          },
        },
        selectHeaderStep: {
          title: t("selectHeader"),
          nextButtonTitle: ct("next"),
          backButtonTitle: ct("back"),
        },
        manifestTitle: {
          filterSwitchTitle: t("showOnlyErrors"),
        },
        matchColumnsStep: {
          title: t("matchColumns"),
          nextButtonTitle: ct("next"),
          backButtonTitle: ct("back"),
          userTableTitle: t("userTable"),
          templateTitle: t("templateTitle"),
          selectPlaceholder: t("selectColumn"),
          ignoredColumnText: t("columnIgnored"),
          subSelectPlaceholder: t("subSelectPlaceholder"),
          matchDropdownTitle: t("matchTitle"),
          unmatched: t("unmatchedTitle"),
          duplicateColumnWarningTitle: t("duplicateTitle"),
          duplicateColumnWarningDescription: t("duplicateDescription"),
        },
        validationStep: {
          title: t("validateTitle"),
          nextButtonTitle: ct("confirm"),
          backButtonTitle: ct("back"),
          noRowsMessage: t("noData"),
          noRowsMessageWhenFiltered: t("noDataFiltered"),
          discardButtonTitle: t("discardBtn"),
          filterSwitchTitle: t("filterBtn"),
        },
        alerts: {
          confirmClose: {
            headerTitle: t("exitImport"),
            bodyText: t("confirmExit"),
            cancelButtonTitle: ct("cancel"),
            exitButtonTitle: t("exitFlowBtn"),
          },
          submitIncomplete: {
            headerTitle: t("errorsDetected"),
            bodyText: t("errorsDetectedDescription"),
            bodyTextSubmitForbidden: t("errorsDetectedForbidden"),
            cancelButtonTitle: ct("cancel"),
            finishButtonTitle: ct("submit"),
          },
          submitError: {
            title: ct("error"),
            defaultMessage: t("submitErrorMsg"),
          },
          unmatchedRequiredFields: {
            headerTitle: t("unmatchedRequiredTitle"),
            bodyText: t("unmatchedRequiredDescription"),
            listTitle: t("unmatchedListTitle"),
            cancelButtonTitle: ct("cancel"),
            continueButtonTitle: ct("continue"),
          },
          toast: {
            error: ct("error"),
          },
        },
      }}
      customTheme={{
        colors: {
          textColor: theme === "light" ? "#2D3748" : "#aeb6c0",
          subtitleColor: theme === "light" ? "#718096" : "#A0AEC0",
          inactiveColor: theme === "light" ? "#A0AEC0" : "#718096",
          border: theme === "light" ? "#E2E8F0" : "#61718b",
          background: theme === "light" ? "white" : "rgb(var(--panel-color))",
          backgroundAlpha: "rgba(255,255,255,0)",
          secondaryBackground: theme === "light" ? "#EDF2F7" : "#1f232a",
          highlight: theme === "light" ? "#E2E8F0" : "#2D3748",
          rsi: {
            "50": "rgba(var(--gail-color-primary) / 0.3)",
            "100": "rgba(var(--gail-color-primary) / 0.5)",
            "200": "rgba(var(--gail-color-primary) / 0.5)",
            "300": "rgba(var(--gail-color-primary) / 0.7)",
            "400": "rgba(var(--gail-color-primary) / 0.7)",
            "500": "rgb(var(--gail-color-primary))",
            "600": "rgba(var(--gail-color-primary) / 0.7)",
            "700": "rgba(var(--gail-color-primary) / 0.7)",
            "800": "rgba(var(--gail-color-primary) / 0.5)",
            "900": "rgba(var(--gail-color-primary) / 0.5)",
          },
        },
      }}
    />
  );
}
